import {
  createApprovalRequest,
  createAutomationPolicy,
  createContextFact,
  createPlanningRun,
  createReviewReport,
  decideApprovalRequest,
  listApprovalRequests,
  listAutomationPolicies,
  listContextFacts,
  listExecutionRuns,
  listPlanningRuns,
  listReviewReports,
  listScheduleBlocks,
  listTasks,
} from '@portfolio-pm/yjs-doc';
import { CalendarClock, CheckCircle2, CircleAlert, Play, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDocStore } from '../../store/doc-store.ts';
import { ScrollViewport } from '../layout/ScrollViewport.tsx';
import { AccessSessionExpiredError, calendarApiFetch } from './calendar-api.ts';

type AgentTab = 'runs' | 'schedule' | 'approvals' | 'reviews' | 'context';
const DAY = 86_400_000;

export function AgentWorkspace(): JSX.Element {
  const store = getDocStore();
  const [tab, setTab] = useState<AgentTab>('runs');
  const [, setRevision] = useState(0);
  useEffect(() => {
    const update = (): void => setRevision((value) => value + 1);
    store.doc.on('update', update);
    return () => store.doc.off('update', update);
  }, [store.doc]);

  const runs = listPlanningRuns(store.doc);
  const schedule = listScheduleBlocks(store.doc);
  const approvals = listApprovalRequests(store.doc);
  const executions = listExecutionRuns(store.doc);
  const reviews = listReviewReports(store.doc);
  const contextFacts = listContextFacts(store.doc, true);
  const pendingApprovals = approvals.filter((approval) => approval.state === 'pending');

  const startRun = (): void => {
    const now = Date.now();
    createPlanningRun(store.doc, {
      trigger: 'manual',
      horizonStart: now,
      horizonEnd: now + 7 * DAY,
      state: 'awaiting_llm',
      currentStage: 'prioritization',
      inputFingerprint: `ui:${now}`,
    });
  };

  return (
    <section className="agent-control" aria-label="Agent Control">
      <div className="agent-control-header">
        <div className="agent-control-header-inner">
          <div className="agent-control-heading-row">
            <div className="min-w-0 max-w-3xl">
              <p className="eyebrow-label">Hybrid workflow</p>
              <h2 className="agent-control-title">Agent Control</h2>
              <p className="agent-control-intro">
                Portfolio PM owns approvals and side effects. Use “Run PPM agent chain” in your MCP
                client for judgment-heavy stages.
              </p>
            </div>
            <button type="button" className="ui-button ui-button-primary" onClick={startRun}>
              <Play aria-hidden="true" size={15} /> Start seven-day plan
            </button>
          </div>
          <div className="agent-control-tabs" role="tablist" aria-label="Agent Control views">
            {(['runs', 'schedule', 'approvals', 'reviews', 'context'] as AgentTab[]).map(
              (value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={tab === value}
                  className="agent-control-tab"
                  onClick={() => setTab(value)}
                >
                  {label(value)}
                  {value === 'approvals' && pendingApprovals.length
                    ? ` (${pendingApprovals.length})`
                    : ''}
                </button>
              ),
            )}
          </div>
        </div>
      </div>
      <ScrollViewport
        className="min-h-0 flex-1"
        contentClassName="agent-control-content"
        showIndicator
      >
        {tab === 'runs' && <RunsPanel runs={runs} executions={executions} />}
        {tab === 'schedule' && <SchedulePanel blocks={schedule} />}
        {tab === 'approvals' && <ApprovalsPanel approvals={approvals} />}
        {tab === 'reviews' && <ReviewsPanel reviews={reviews} />}
        {tab === 'context' && <ContextPanel facts={contextFacts} />}
      </ScrollViewport>
    </section>
  );
}

function RunsPanel({
  runs,
  executions,
}: {
  runs: ReturnType<typeof listPlanningRuns>;
  executions: ReturnType<typeof listExecutionRuns>;
}): JSX.Element {
  if (!runs.length)
    return (
      <Empty
        title="No planning runs"
        detail="Start a seven-day plan, then activate the PPM MCP prompt to refine it."
      />
    );
  return (
    <div className="grid gap-3">
      {runs.map((run) => (
        <article key={run.id} className="agent-control-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold capitalize">{run.currentStage.replace('_', ' ')}</p>
              <p className="agent-control-secondary mt-1">
                {formatDate(run.horizonStart)} – {formatDate(run.horizonEnd)} · {run.trigger}
              </p>
            </div>
            <Status value={run.state} />
          </div>
          <p className="agent-control-meta mt-3">
            {executions.filter((execution) => execution.planningRunId === run.id).length} execution
            run(s) · ID {run.id}
          </p>
        </article>
      ))}
    </div>
  );
}

function SchedulePanel({ blocks }: { blocks: ReturnType<typeof listScheduleBlocks> }): JSX.Element {
  const store = getDocStore();
  if (!blocks.length)
    return (
      <Empty
        title="No schedule proposals"
        detail="Scheduling produces a primary slot and up to two alternatives. Nothing is booked automatically."
      />
    );
  return (
    <div className="grid gap-3">
      {blocks.map((block) => (
        <article key={block.id} className="agent-control-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 font-semibold">
                <CalendarClock aria-hidden="true" size={16} />
                {formatDateTime(block.startAt)} – {formatTime(block.endAt)}
              </p>
              <p className="agent-control-secondary mt-2">{block.explanation}</p>
            </div>
            <Status value={block.state} />
          </div>
          {block.state === 'proposed' && (
            <button
              type="button"
              className="ui-button mt-3"
              onClick={() =>
                createApprovalRequest(store.doc, {
                  planningRunId: block.planningRunId,
                  action: 'Book proposed focus block',
                  connector: 'google_calendar',
                  operation: 'events.insert',
                  diff: [
                    {
                      path: `scheduleBlocks.${block.id}.state`,
                      before: 'proposed',
                      after: 'booked',
                    },
                  ],
                  risk: 'medium',
                  inputFingerprint: `${block.id}:${block.updatedAt}:${block.startAt}:${block.endAt}`,
                })
              }
            >
              Request booking approval
            </button>
          )}
        </article>
      ))}
    </div>
  );
}

function ApprovalsPanel({
  approvals,
}: { approvals: ReturnType<typeof listApprovalRequests> }): JSX.Element {
  const store = getDocStore();
  if (!approvals.length)
    return (
      <Empty
        title="No approval requests"
        detail="Consequential writes appear here with their exact diff and risk."
      />
    );
  return (
    <div className="grid gap-3">
      {approvals.map((approval) => (
        <article key={approval.id} className="agent-control-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold">{approval.action}</p>
              <p className="agent-control-secondary mt-1">
                {approval.connector} · {approval.operation} · {approval.risk} risk
              </p>
            </div>
            <Status value={approval.state} />
          </div>
          <ul className="mt-3 space-y-1 text-sm">
            {approval.diff.map((change) => (
              <li key={change.path}>
                <span className="font-medium">{change.path}</span>:{' '}
                {String(change.before ?? 'empty')} → {String(change.after ?? 'empty')}
              </li>
            ))}
          </ul>
          {approval.state === 'pending' && (
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                className="ui-button ui-button-primary"
                onClick={() => {
                  const confirmation = window.prompt(
                    `Type exactly: ${approval.requiredConfirmation}`,
                  );
                  if (confirmation !== null) {
                    try {
                      decideApprovalRequest(
                        store.doc,
                        approval.id,
                        'approved',
                        'local-user',
                        confirmation,
                      );
                    } catch (error) {
                      window.alert(error instanceof Error ? error.message : 'Approval failed');
                    }
                  }
                }}
              >
                Approve
              </button>
              <button
                type="button"
                className="ui-button"
                onClick={() =>
                  decideApprovalRequest(store.doc, approval.id, 'rejected', 'local-user', '')
                }
              >
                Reject
              </button>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

function ReviewsPanel({ reviews }: { reviews: ReturnType<typeof listReviewReports> }): JSX.Element {
  const store = getDocStore();
  const [reviewHour, setReviewHour] = useState(new Date().getHours());
  const policies = listAutomationPolicies(store.doc).filter(
    (policy) =>
      policy.connector === 'portfolio_pm' && policy.operations.includes('review.generate'),
  );
  const generate = (period: 'daily' | 'weekly'): void => {
    const end = Date.now();
    const start = end - (period === 'daily' ? DAY : 7 * DAY);
    const tasks = listTasks(store.doc);
    const completedTaskIds = tasks
      .filter(
        (task) =>
          task.completedAt !== undefined && task.completedAt >= start && task.completedAt < end,
      )
      .map((task) => task.id);
    const slippedTaskIds = tasks
      .filter(
        (task) =>
          task.completedAt === undefined &&
          task.trashedAt === undefined &&
          task.scheduledAt !== undefined &&
          task.scheduledAt < end,
      )
      .map((task) => task.id);
    createReviewReport(store.doc, {
      period,
      periodStart: start,
      periodEnd: end,
      completedTaskIds,
      slippedTaskIds,
      changedPriorityTaskIds: [],
      observations: [
        `${completedTaskIds.length} task(s) completed.`,
        `${slippedTaskIds.length} scheduled task(s) still open.`,
      ],
      recommendations: [],
    });
  };
  const enableSchedule = (cadence: 'daily' | 'weekly'): void => {
    createAutomationPolicy(store.doc, {
      name: `${cadence} factual review`,
      enabled: true,
      connector: 'portfolio_pm',
      operations: ['review.generate'],
      maximumRisk: 'low',
      contextIds: [],
      schedule: {
        cadence,
        localHour: reviewHour,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        ...(cadence === 'weekly' ? { weekday: 1 } : {}),
      },
    });
  };
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button type="button" className="ui-button" onClick={() => generate('daily')}>
          Run daily review
        </button>
        <button type="button" className="ui-button" onClick={() => generate('weekly')}>
          Run weekly review
        </button>
        <label className="ui-chip">
          Review hour{' '}
          <input
            aria-label="Scheduled review hour"
            className="ml-2 w-12 bg-transparent"
            type="number"
            min={0}
            max={23}
            value={reviewHour}
            onChange={(event) => setReviewHour(Number(event.target.value))}
          />
        </label>
        <button type="button" className="ui-button" onClick={() => enableSchedule('daily')}>
          Schedule daily
        </button>
        <button type="button" className="ui-button" onClick={() => enableSchedule('weekly')}>
          Schedule weekly
        </button>
      </div>
      {policies.length > 0 && (
        <p className="agent-control-meta mb-3">
          {policies.length} scheduled review policy/policies enabled.
        </p>
      )}
      {reviews.length ? (
        <div className="grid gap-3">
          {reviews.map((review) => (
            <article key={review.id} className="agent-control-card">
              <div className="flex justify-between gap-3">
                <p className="font-semibold capitalize">{review.period} review</p>
                <span className="agent-control-secondary">{formatDate(review.periodEnd)}</span>
              </div>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
                {review.observations.map((observation) => (
                  <li key={observation}>{observation}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          title="No reviews yet"
          detail="Daily and weekly reviews record facts first; an MCP prompt can add a narrative without inventing causes."
        />
      )}
    </div>
  );
}

function ContextPanel({ facts }: { facts: ReturnType<typeof listContextFacts> }): JSX.Element {
  const store = getDocStore();
  const [status, setStatus] = useState<string>('');
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendars, setCalendars] = useState<
    Array<{ id: string; summary: string; primary: boolean }>
  >([]);
  const [readCalendarIds, setReadCalendarIds] = useState<string[]>([]);
  const [writeCalendarId, setWriteCalendarId] = useState('');
  const [calendarBusy, setCalendarBusy] = useState(false);
  const [calendarChecked, setCalendarChecked] = useState(false);
  const [accessSignInRequired, setAccessSignInRequired] = useState(false);
  const apiBase = useMemo(() => String(import.meta.env.VITE_API_URL ?? '').replace(/\/$/, ''), []);
  const refreshConnection = useCallback(async (): Promise<void> => {
    if (!apiBase) return;
    setCalendarBusy(true);
    setStatus('Checking Google Calendar connection…');
    try {
      const statusResponse = await calendarApiFetch(`${apiBase}/calendar/status`);
      const connection = (await statusResponse.json()) as {
        connected?: boolean;
        calendars?: Array<{
          calendar_id: string;
          read_enabled: number;
          write_enabled: number;
        }>;
        error?: string;
      };
      if (!statusResponse.ok) throw new Error(connection.error ?? 'Connection check failed');
      setAccessSignInRequired(false);
      setCalendarConnected(Boolean(connection.connected));
      if (!connection.connected) {
        setCalendars([]);
        setStatus('Google Calendar is not connected yet.');
        return;
      }

      const calendarResponse = await calendarApiFetch(`${apiBase}/calendar/calendars`);
      const calendarList = (await calendarResponse.json()) as {
        items?: Array<{ id?: string; summary?: string; primary?: boolean }>;
        error?: string;
      };
      if (!calendarResponse.ok) throw new Error(calendarList.error ?? 'Could not load calendars');
      const available = (calendarList.items ?? [])
        .filter((calendar): calendar is { id: string; summary?: string; primary?: boolean } =>
          Boolean(calendar.id),
        )
        .map((calendar) => ({
          id: calendar.id,
          summary: calendar.summary ?? calendar.id,
          primary: Boolean(calendar.primary),
        }));
      const configured = connection.calendars ?? [];
      const configuredReads = configured
        .filter((calendar) => calendar.read_enabled === 1)
        .map((calendar) => calendar.calendar_id);
      const configuredWrite = configured.find(
        (calendar) => calendar.write_enabled === 1,
      )?.calendar_id;
      const defaultCalendar =
        available.find((calendar) => calendar.primary)?.id ?? available[0]?.id ?? '';
      setCalendars(available);
      setReadCalendarIds(
        configuredReads.length > 0 ? configuredReads : defaultCalendar ? [defaultCalendar] : [],
      );
      setWriteCalendarId(configuredWrite ?? defaultCalendar);
      setStatus('Google Calendar connected. Choose the calendars Portfolio PM may use.');
    } catch (error) {
      setCalendarConnected(false);
      setAccessSignInRequired(error instanceof AccessSessionExpiredError);
      setStatus(error instanceof Error ? error.message : 'Connection check failed');
    } finally {
      setCalendarChecked(true);
      setCalendarBusy(false);
    }
  }, [apiBase]);

  useEffect(() => {
    void refreshConnection();
  }, [refreshConnection]);

  const signInAgain = (): void => {
    window.open(`${apiBase}/calendar/status`, '_blank', 'noopener,noreferrer');
    setStatus('PPM sign-in opened. After signing in, return here and refresh the connection.');
  };
  const saveCalendarConfiguration = async (): Promise<void> => {
    if (!writeCalendarId) {
      setStatus('Choose one calendar for approved Portfolio PM bookings.');
      return;
    }
    setCalendarBusy(true);
    try {
      const response = await calendarApiFetch(`${apiBase}/calendar/configure`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ readCalendarIds, writeCalendarId }),
      });
      const result = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(result.error ?? 'Calendar configuration failed');
      if (!readCalendarIds.includes(writeCalendarId)) {
        setReadCalendarIds((current) => [...current, writeCalendarId]);
      }
      setStatus('Calendar choices saved. Approved schedule blocks can now be booked.');
    } catch (error) {
      setAccessSignInRequired(error instanceof AccessSessionExpiredError);
      setStatus(error instanceof Error ? error.message : 'Calendar configuration failed');
    } finally {
      setCalendarBusy(false);
    }
  };
  const addWorkContext = (): void => {
    const now = Date.now();
    createContextFact(store.doc, {
      scope: 'work',
      key: 'working_hours',
      value: '09:00-17:30',
      state: 'confirmed',
      sensitivity: 'normal',
      sourceType: 'user',
      observedAt: now,
      freshUntil: now + 90 * DAY,
      confirmedBy: 'local-user',
    });
  };
  return (
    <div>
      <div className="agent-control-card">
        <h3 className="font-semibold">Connections</h3>
        <p className="agent-control-secondary mt-1">
          Google Calendar is app-owned. Tokens and full selected-calendar event payloads stay
          encrypted in the integrations service.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {calendarChecked && !calendarConnected && !accessSignInRequired && apiBase && (
            <a
              className="ui-button ui-button-primary"
              href={`${apiBase}/calendar/authorize`}
              target="_blank"
              rel="noreferrer"
            >
              Authorise Google Calendar
            </a>
          )}
          <button
            type="button"
            className="ui-button"
            onClick={() => void refreshConnection()}
            disabled={!apiBase || calendarBusy}
          >
            Refresh connection
          </button>
          {accessSignInRequired && (
            <button type="button" className="ui-button" onClick={signInAgain}>
              Sign in again
            </button>
          )}
          <button type="button" className="ui-button" onClick={addWorkContext}>
            Add default work hours
          </button>
        </div>
        {!apiBase && (
          <p className="agent-control-secondary mt-2">
            Calendar connection is disabled until the secured API rollout is configured.
          </p>
        )}
        {status && <output className="mt-2 block text-sm">{status}</output>}
        {calendarConnected && calendars.length > 0 && (
          <div className="mt-4 grid gap-4 border-t border-[color:var(--app-border)] pt-4">
            <fieldset>
              <legend className="text-sm font-semibold">Calendars used for availability</legend>
              <div className="mt-2 grid gap-2">
                {calendars.map((calendar) => (
                  <label key={calendar.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={readCalendarIds.includes(calendar.id)}
                      onChange={(event) =>
                        setReadCalendarIds((current) =>
                          event.target.checked
                            ? [...new Set([...current, calendar.id])]
                            : current.filter((id) => id !== calendar.id),
                        )
                      }
                    />
                    <span>
                      {calendar.summary}
                      {calendar.primary ? ' (primary)' : ''}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="grid gap-2 text-sm font-semibold">
              Calendar for approved bookings
              <select
                className="ui-input"
                value={writeCalendarId}
                onChange={(event) => setWriteCalendarId(event.target.value)}
              >
                <option value="">Choose a calendar</option>
                {calendars.map((calendar) => (
                  <option key={calendar.id} value={calendar.id}>
                    {calendar.summary}
                    {calendar.primary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <div>
              <button
                type="button"
                className="ui-button ui-button-primary"
                onClick={() => void saveCalendarConfiguration()}
                disabled={calendarBusy || !writeCalendarId}
              >
                Save calendar choices
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="agent-control-section-heading">
        <div>
          <h3 className="font-semibold">Context facts</h3>
          <p className="agent-control-secondary mt-1">
            Confirmed working assumptions with a visible source and freshness date.
          </p>
        </div>
        <span className="agent-control-count">{facts.length}</span>
      </div>
      {facts.length > 0 ? (
        <div className="grid gap-3">
          {facts.map((fact) => (
            <article key={fact.id} className="agent-control-card">
              <div className="flex justify-between gap-3">
                <p className="font-semibold">{fact.key}</p>
                <Status value={fact.state} />
              </div>
              <p className="mt-2 text-sm">{fact.value}</p>
              <p className="agent-control-meta mt-2">
                {fact.scope} · {fact.sourceType} · fresh until {formatDate(fact.freshUntil)}
              </p>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          title="No context facts"
          detail="Add default work hours or refresh context through the PPM agent workflow."
        />
      )}
    </div>
  );
}

function Status({ value }: { value: string }): JSX.Element {
  const warning = ['pending', 'awaiting_user', 'awaiting_llm', 'drifted', 'failed'].includes(value);
  return (
    <span className="agent-status capitalize" data-tone={warning ? 'warning' : 'success'}>
      {warning ? (
        <CircleAlert aria-hidden="true" size={12} />
      ) : (
        <CheckCircle2 aria-hidden="true" size={12} />
      )}
      {value.replace('_', ' ')}
    </span>
  );
}

function Empty({ title, detail }: { title: string; detail: string }): JSX.Element {
  return (
    <div className="agent-control-empty">
      <div>
        <span className="agent-control-empty-icon">
          <RefreshCw aria-hidden="true" size={20} />
        </span>
        <h3 className="mt-3 font-semibold">{title}</h3>
        <p className="agent-control-secondary mt-1 max-w-md">{detail}</p>
      </div>
    </div>
  );
}

function label(tab: AgentTab): string {
  return tab.charAt(0).toUpperCase() + tab.slice(1);
}
function formatDate(value: number): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(value);
}
function formatDateTime(value: number): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    value,
  );
}
function formatTime(value: number): string {
  return new Intl.DateTimeFormat(undefined, { timeStyle: 'short' }).format(value);
}
